// "Does this roster patient have an appointment?" — answered from notes-c, not
// from the roster's own `source_record_ids`.
//
// `source_record_ids` is a denormalized snapshot written ONLY by the
// patient-table build job (backend/api/patient_table.py). Nothing prunes it when
// a notes-c row is deleted and nothing extends it when one is created, so it
// drifts in both directions:
//
//   • deleted visit  -> the id stays behind, and the patient looks visited
//                       forever (this is the "Ready, Bruce" case: a roster row
//                       whose only id pointed at a row that no longer exists)
//   • new visit      -> the array stays empty until the build job next runs, so
//                       a freshly scheduled patient still looks never-seen
//
// `vita_id` has neither problem. It is NOT NULL on both patient tables, indexed
// on notes-c, and stamped there by the `trg_notes-c_vita_id` BEFORE INSERT
// trigger, so it exists on a visit row from the moment the visit does.
//
// Phase 4 cutover, site 4 (utils/rdsCutover.js `roster_visits`,
// TEST_INTEGRATION_PLAN.md §17): when both switches are on, each chunk's rows
// are SERVED by RDS while the Supabase read still runs as the parity oracle
// and the fallback — the same contract as the roster query itself. Off (the
// default) this is byte-for-byte the old behaviour, except that the Supabase
// read now selects `id` alongside `vita_id` so the two sides can be compared
// row by row.
import { isCutoverEffective, startVisitCountsFromRds, serveVisitCountsFromRds } from './rdsCutover';

const CHUNK = 200;
// RDS cutover flag / dashboard pilot for this lookup.
const VISITS_CALL_SITE = 'roster_visits';

// Map<vita_id, live notes-c row count> for the given patients, within one schema.
// Only the patients asked about are queried, so cost scales with the page's
// roster rather than with the size of notes-c.
export async function fetchVisitCountsByVitaId(client, schema, vitaIds, { chunkSize = CHUNK, signal } = {}) {
  const counts = new Map();
  const unique = [...new Set((vitaIds || []).filter(Boolean))];
  if (unique.length === 0) return counts;

  // Decided once per lookup (the flag is cached client-side), never per chunk.
  const cutoverOn = await isCutoverEffective(VISITS_CALL_SITE);

  for (let i = 0; i < unique.length; i += chunkSize) {
    const chunk = unique.slice(i, i + chunkSize);
    // Started BEFORE the Supabase read so the two round trips overlap;
    // settled after it, with Supabase's rows in hand.
    const rdsStarted = cutoverOn
      ? startVisitCountsFromRds(schema, { vitaIds: chunk, signal })
      : null;
    let rows;
    try {
      // PostgREST puts .in() lists in the URL, so a whole roster in one call
      // would overflow it.
      // eslint-disable-next-line no-await-in-loop
      const { data, error } = await client
        .schema(schema)
        .from('notes-c')
        .select('id, vita_id')
        .in('vita_id', chunk);
      // A failed chunk costs only its own patients; the rest of the lookup
      // stands. Without Supabase's rows there is no oracle and no fallback,
      // so RDS is not consulted for the chunk either.
      if (error || !data) continue;
      rows = data;
      if (rdsStarted) {
        // RDS's answer (its own row-level authorization applied) is what is
        // counted; any RDS failure resolves to the Supabase rows already
        // fetched. Either way the outcome is reported.
        // eslint-disable-next-line no-await-in-loop
        const served = await serveVisitCountsFromRds(schema, {
          vitaIds: chunk, supabaseRows: rows, started: rdsStarted,
        });
        rows = served.rows;
      }
    } catch (_) {
      continue;
    }
    rows.forEach((r) => {
      if (!r || !r.vita_id) return;
      counts.set(r.vita_id, (counts.get(r.vita_id) || 0) + 1);
    });
  }
  return counts;
}

// Attach live visit facts to roster rows: `_hasVisits` and `_visitCount`.
//
// The stale snapshot is consulted ONLY for a row with no vita_id. Both patient
// tables declare it NOT NULL so that should be unreachable, but treating such a
// row as "no appointment" would quietly file a real patient under never-seen,
// which is the very failure this module exists to remove.
export function annotateVisitState(rows, counts) {
  return (rows || []).map((row) => {
    if (!row.vita_id) {
      const legacy = Array.isArray(row.source_record_ids) ? row.source_record_ids.length : 0;
      return { ...row, _visitCount: legacy, _hasVisits: legacy > 0 };
    }
    const n = counts.get(row.vita_id) || 0;
    return { ...row, _visitCount: n, _hasVisits: n > 0 };
  });
}
